import { StyleSheet, TextStyle, ViewStyle, Platform } from 'react-native';
import { DesignTokens } from '../types/tokens';

/**
 * Convert spacing token (e.g., "16px") to number for React Native
 */
function spacingToNumber(spacing: string): number {
  const match = spacing.match(/(\d+(?:\.\d+)?)/);
  return match ? parseFloat(match[1]) : 0;
}

/**
 * Convert font size token to number
 */
function fontSizeToNumber(size: string): number {
  const match = size.match(/(\d+(?:\.\d+)?)/);
  return match ? parseFloat(match[1]) : 16;
}

/**
 * Add opacity to a hex color
 * @param color Hex color (e.g., "#FF0000")
 * @param opacity Opacity value 0-1 (e.g., 0.2 for 20%)
 * @returns Hex color with opacity (e.g., "#FF000033" for 20% opacity)
 */
export function addColorOpacity(color: string, opacity: number): string {
  // Remove # if present
  const hex = color.replace('#', '');
  
  // Convert opacity to hex (0-255)
  const opacityHex = Math.round(opacity * 255).toString(16).padStart(2, '0').toUpperCase();
  
  return `#${hex}${opacityHex}`;
}

/**
 * Create StyleSheet utilities from design tokens
 */
export function createStyles(tokens: DesignTokens) {
  return {
    // Spacing utilities
    spacing: {
      xs: spacingToNumber(tokens.spacing.xs),
      sm: spacingToNumber(tokens.spacing.sm),
      md: spacingToNumber(tokens.spacing.md),
      lg: spacingToNumber(tokens.spacing.lg),
      xl: spacingToNumber(tokens.spacing.xl),
      '2xl': spacingToNumber(tokens.spacing['2xl']),
      '3xl': spacingToNumber(tokens.spacing['3xl']),
    },
    
    // Typography utilities
    typography: {
      body: {
        // Use system fonts - React Native Web doesn't load custom fonts automatically
        fontFamily: Platform.select({
          web: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
          default: tokens.typography.family.body[0] || 'System',
        }),
        fontSize: fontSizeToNumber(tokens.typography.size.base),
        lineHeight: fontSizeToNumber(tokens.typography.size.base) * tokens.typography.lineHeight.normal,
      } as TextStyle,
      heading: {
        fontFamily: Platform.select({
          web: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
          default: tokens.typography.family.heading[0] || 'System',
        }),
        fontWeight: tokens.typography.weight.bold as any,
      } as TextStyle,
      sizes: {
        xs: fontSizeToNumber(tokens.typography.size.xs),
        sm: fontSizeToNumber(tokens.typography.size.sm),
        base: fontSizeToNumber(tokens.typography.size.base),
        lg: fontSizeToNumber(tokens.typography.size.lg),
        xl: fontSizeToNumber(tokens.typography.size.xl),
        '2xl': fontSizeToNumber(tokens.typography.size['2xl']),
        '3xl': fontSizeToNumber(tokens.typography.size['3xl']),
        '4xl': fontSizeToNumber(tokens.typography.size['4xl']),
        '5xl': fontSizeToNumber(tokens.typography.size['5xl']),
        '6xl': fontSizeToNumber(tokens.typography.size['6xl']),
      },
      weights: {
        regular: tokens.typography.weight.regular as any,
        medium: tokens.typography.weight.medium as any,
        semibold: tokens.typography.weight.semibold as any,
        bold: tokens.typography.weight.bold as any,
      },
      lineHeight: {
        tight: tokens.typography.lineHeight.tight,
        normal: tokens.typography.lineHeight.normal,
        relaxed: tokens.typography.lineHeight.relaxed,
      },
    },
    
    // Radius utilities
    radius: {
      sm: spacingToNumber(tokens.radius.sm),
      md: spacingToNumber(tokens.radius.md),
      lg: spacingToNumber(tokens.radius.lg),
      xl: spacingToNumber(tokens.radius.xl),
      full: 9999, // Full radius
    },
    
    // Color utilities
    colors: tokens.color,
  };
}

/**
 * Create common style patterns
 */
export function createCommonStyles(tokens: DesignTokens) {
  const utils = createStyles(tokens);
  
  return StyleSheet.create({
    container: {
      flex: 1,
      backgroundColor: tokens.color.background,
    },
    centered: {
      flex: 1,
      alignItems: 'center',
      justifyContent: 'center',
    },
    row: {
      flexDirection: 'row',
    },
    button: {
      backgroundColor: tokens.color.primary,
      paddingHorizontal: utils.spacing.lg,
      paddingVertical: utils.spacing.md,
      borderRadius: utils.radius.md,
      alignItems: 'center',
      justifyContent: 'center',
    },
    buttonText: {
      color: tokens.color.onPrimary,
      fontSize: utils.typography.sizes.base,
      fontWeight: utils.typography.weights.semibold,
    },
    input: {
      borderWidth: 1,
      borderColor: tokens.color.border,
      borderRadius: utils.radius.md,
      paddingHorizontal: utils.spacing.md,
      paddingVertical: utils.spacing.md,
      backgroundColor: tokens.color.surface,
      color: tokens.color.onSurface,
      fontSize: utils.typography.sizes.base,
    },
    card: {
      backgroundColor: tokens.color.surface,
      borderRadius: utils.radius.lg,
      padding: utils.spacing.lg,
      borderWidth: 1,
      borderColor: tokens.color.border,
    },
    text: {
      color: tokens.color.onBackground,
      fontSize: utils.typography.sizes.base,
      fontFamily: utils.typography.body.fontFamily,
    },
    heading: {
      color: tokens.color.onBackground,
      fontFamily: utils.typography.heading.fontFamily,
      fontWeight: utils.typography.weights.bold,
    },
  });
}

